import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Spinner } from './components/ui';
import Layout from './components/Layout';

// Routes are split so a phone scanning a QR code downloads the share page and
// nothing else — not the dashboard builder or the chart library it needs.
const Login = lazy(() => import('./pages/Login'));
const Setup = lazy(() => import('./pages/Setup'));
const PublicShare = lazy(() => import('./pages/PublicShare'));
const Overview = lazy(() => import('./pages/Overview'));
const Gateways = lazy(() => import('./pages/Gateways'));
const GatewayDetail = lazy(() => import('./pages/GatewayDetail'));
const Devices = lazy(() => import('./pages/Devices'));
const DeviceDetail = lazy(() => import('./pages/DeviceDetail'));
const Discovery = lazy(() => import('./pages/Discovery'));
const Dashboards = lazy(() => import('./pages/Dashboards'));
const DashboardView = lazy(() => import('./pages/DashboardView'));
const Alarms = lazy(() => import('./pages/Alarms'));
const AlarmRules = lazy(() => import('./pages/AlarmRules'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Reports = lazy(() => import('./pages/Reports'));
const Playlists = lazy(() => import('./pages/Playlists'));
const Locations = lazy(() => import('./pages/Locations'));
const Controls = lazy(() => import('./pages/Controls'));
const WorkOrders = lazy(() => import('./pages/WorkOrders'));
const Settings = lazy(() => import('./pages/Settings'));
const PlatformConsole = lazy(() => import('./pages/PlatformConsole'));
const ClientConsole = lazy(() => import('./pages/ClientConsole'));
const Roles = lazy(() => import('./pages/Roles'));
const NotFound = lazy(() => import('./pages/NotFound'));

const Loading = () => (
  <div className="flex items-center justify-center h-screen"><Spinner className="h-6 w-6" /></div>
);

export default function App() {
  const { status, scope } = useAuth();

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public share pages work with no session at all. */}
        <Route path="/s/:token" element={<PublicShare />} />

        {status === 'loading' && <Route path="*" element={<Loading />} />}

        {status === 'setup' && (
          <>
            <Route path="/setup" element={<Setup />} />
            <Route path="*" element={<Navigate to="/setup" replace />} />
          </>
        )}

        {status === 'anonymous' && (
          <>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        )}

        {status === 'authenticated' && (
          <Route element={<Layout />}>
            <Route path="/" element={
              scope?.kind === 'platform' ? <Navigate to="/platform" replace />
              : scope?.kind === 'org' ? <Navigate to="/client" replace />
              : <Overview />
            } />
            <Route path="/dashboards" element={<Dashboards />} />
            <Route path="/dashboards/:id" element={<DashboardView />} />
            <Route path="/devices" element={<Devices />} />
            <Route path="/devices/:id" element={<DeviceDetail />} />
            <Route path="/discovery" element={<Discovery />} />
            <Route path="/gateways" element={<Gateways />} />
            <Route path="/gateways/:id" element={<GatewayDetail />} />
            <Route path="/alarms" element={<Alarms />} />
            <Route path="/alarm-rules" element={<AlarmRules />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/playlists" element={<Playlists />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/controls" element={<Controls />} />
            <Route path="/work-orders" element={<WorkOrders />} />
            <Route path="/settings/*" element={<Settings />} />
            <Route path="/roles" element={<Roles />} />
            <Route path="/platform" element={<PlatformConsole />} />
            <Route path="/client" element={<ClientConsole />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        )}
      </Routes>
    </Suspense>
  );
}
